import axiosInstance from "../axios";
import Stripe from "stripe";
import { getLoginToken } from "../storage";
import { BaseUrl, COLLECTIONS, DOMAINURL, EMP_DOMAIN, EMP_ROLES, STRIPE_SECRETE } from "../config";
import {
  collection,
  query,
  where,
  getDocs,
  documentId,
  serverTimestamp,
  doc,
  setDoc,
  updateDoc,
  addDoc,
  getDoc,
  limit,
  writeBatch,
} from "firebase/firestore";
import { firebase } from "../firebase-config";
import { fetchSignInMethodsForEmail, getAuth } from "firebase/auth";
import EmailTemplate from "../../app/auth/EmailTemplate";
import moment from "moment";
const stripe = new Stripe(STRIPE_SECRETE);

const defaultHeaders = {
  "Content-Type": "application/json",
  withCredentials: true,
};

let userCollection = COLLECTIONS.USERS;

/**
 * Unique validate user by email and phone number
 * @param {*} formData
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function validateUniqueUser(formData, onSuccess, onError) {
  let responseObj = {};
  let isValid = true;
  const auth = getAuth();

  // Check for email
  if (formData.email && formData.email !== "") {
    const userRef = collection(firebase, userCollection);
    const emailQuery = query(userRef, where("email", "==", formData.email));
    const snapshot = await getDocs(emailQuery);
  
    let emailConflict = false;
  
    snapshot.forEach((doc) => {
      const data = doc.data();
  
      const isSameUser = formData.userId && formData.userId === data.userId;
      const isDeleted = data.deleted === true;
  
      if (!isDeleted && !isSameUser) {
        emailConflict = true;
      }
    });
  
    let firebaseEmailConflict = false;
    try {
      const signInMethods = await fetchSignInMethodsForEmail(auth, formData.email);
      if (signInMethods.length > 0) {
        firebaseEmailConflict = true;
      }
    } catch (err) {
      console.log("Auth email check failed:", err);
    }

    if (emailConflict || firebaseEmailConflict) {
      responseObj = { ...responseObj, email: "Email Already Registered" };
      isValid = false;
    }
  }

  if (formData.mobile && formData.mobile !== "") {
    const userRef = collection(firebase, userCollection);
    const mobileQuery = query(userRef, where("phoneNumber", "==", formData.mobile));
    const snapshotMobile = await getDocs(mobileQuery);
  
    let hasConflict = false;
  
    snapshotMobile.forEach((doc) => {
      const data = doc.data();
  
      if (data.deleted !== true && data.userId !== formData.userId) {
        hasConflict = true;
      }
    });
  
    if (hasConflict) {
      isValid = false;
      responseObj = {
        ...responseObj,
        mobile: "Mobile Number Already Registered",
        phoneNumber: "Mobile Number Already Registered",
      };
    }
  }

  onSuccess({ status: isValid, data: responseObj });
}

/**
 * Get list of client users (Not used after restructured)
 * @param {*} formData
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function getClientList(formData, onSuccess, onError) {
  const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
  try {
    const res = await axiosInstance.get(`${BaseUrl}/clients`, {
      params: { ...formData },
      headers: { ...defaultHeaders, ...authHeaders },
    });
    onSuccess && onSuccess(res);
  } catch (error) {
    onError && onError(error);
  }
}

/**
 * Get list of users (Not used after restructured)
 * @param {*} formData
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function getUserListBasedOnIds(userIDs, onSuccess, onError) {
  const chunk = (arr, chunkSize) => {
    const R = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
      R.push(arr.slice(i, i + chunkSize));
    }
    return R;
  };

  const usersNew = [];
  try {
    const userChunks = chunk(userIDs, 10);
    for (const chunkIds of userChunks) {
      const colRef = collection(firebase, userCollection);
      const q = query(colRef, where(documentId(), "in", chunkIds));
      const snap = await getDocs(q);

      snap.forEach((doc) => {
        const userData = { ...doc.data(), id: doc.id };
        usersNew.push(userData);
      });
    }
    onSuccess && onSuccess(usersNew);
  } catch (error) {
    console.error("Error getting documents: ", error);
    onError && onError(error);
  }
}

async function createUsers(formData) {
  const currentUserId = getAuth().currentUser.uid;

  try {
    const userDocRef = doc(firebase, COLLECTIONS.USERS, formData?.userId);

    await setDoc(userDocRef, {
      ...formData,
      createdBy: currentUserId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    const responseItem = {
      ...formData,
      id: formData?.userId,
    };
    return { status: true, data: responseItem };
  } catch (error) {
    return { status: false, error: error.message || error };
  }
}

export async function updateStripeCustomerName(customerId, fullName) {
  await stripe.customers.update(customerId, { name: fullName });
}

const getDisplayRole = (role, employeerole) => {
  const rolesArray = ([])?.concat(role || []);

  const displayRoles = rolesArray.map(r => {
    if (r === "admin") return "Sys. Admin";
    if (r === "Employee") return employeerole === "Admin" ? "Team Admin" : employeerole || "Employee";
    return r;
  });

  return displayRoles?.join(", ");
};

const areArraysEqual = (a, b) => {
  if (!a || !b) return false;
  if (a?.length !== b?.length) return false;
  return [...a]?.sort()?.every((val, index) => val === [...b]?.sort()[index]);
};


export async function saveOrUpdateUserData(formData, id) {
  const auth = getAuth();
  const currentUserId = auth.currentUser?.uid;

  try {
    if (!currentUserId) {
      throw new Error("No authenticated user found");
    }

    let newData = null;

    if (id) {
      const userDocRef = doc(firebase, COLLECTIONS.USERS, id);

      const existingUserSnap = await getDoc(userDocRef);
      const existingUser = existingUserSnap.data();

      if (formData?.phoneNumber && formData?.phoneNumber !== existingUser.phoneNumber) {
        const phoneQuery = query(
          collection(firebase, COLLECTIONS.USERS),
          where("phoneNumber", "==", formData?.phoneNumber),
          where("status", "==", true),
          limit(1)
        );
        const phoneSnapshot = await getDocs(phoneQuery);

        if (!phoneSnapshot.empty) {
          return {
            status: false,
            field: "phone",
            message: "A user with this phone number already exists.",
          };
        }
      }

      const isClientTeam = formData?.employeerole === EMP_ROLES.CLIENT_TEAM;
      const roleChanged = !areArraysEqual(existingUser?.role, formData?.role);
      const EmpRoleChanged = (existingUser?.employeerole ?? null) !== (formData?.employeerole ?? null);
      const shouldGeneratePassword = roleChanged || (isClientTeam && EmpRoleChanged)
      const role = formData?.role;
  
      if (shouldGeneratePassword && (!role || (Array.isArray(role) ? !role.includes("companyUser") : role !== "companyUser"))) {
        const msg = {
          to: formData?.email,
          subject: `Your ProTeams account was successfully updated`,
          html: `
            <div style="font-family: Arial, sans-serif; color: #333; max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 10px; overflow: hidden;">
            <div style="background-color:#f4f6f8; padding:20px; text-align:center;">
              <img
                src="https://${DOMAINURL}/csmGrayTextLogin.png"
                alt="ProTeams Logo"
                width="180"
                style="display:block; margin:0 auto; max-width:180px; width:100%; height:auto; border:0;"
              />
            </div>

            <div style="padding:25px 20px;">
      
                  <h2 style="color: #078037;">Welcome to ProTeams!</h2>
                  <p style="font-size: 16px;"><strong>Hello ${formData?.firstName},</strong></p>
      
                  <p style="font-size: 15px;">
                    ${
                      isClientTeam
                        ? `You've been invited to the <strong>ProTeams</strong> mobile app. Click the button below to download:`
                        : `Welcome to the <strong>ProTeams</strong> Web Portal. You can access the web platform using the credentials below:`
                    }
                  </p>
      
                  <p style="text-align: center;">
                    <a href="https://${
                      isClientTeam ? `${EMP_DOMAIN}/apps/` : DOMAINURL
                    }" 
                      style="display: inline-block; background-color: #078037; color: white; text-decoration: none; padding: 10px 20px; border-radius: 5px; font-weight: bold;">
                      ${
                        isClientTeam
                          ? "Download ProTeams App"
                          : "Go to ProTeams Web Portal"
                      }
                    </a>
                  </p>
      
                  <hr style="margin: 20px 0;">
      
                  <p style="font-size: 15px;">Here are your login credentials:</p>
      
                  <p style="font-size: 16px;"><strong>Username:</strong> ${formData?.email}</p>
                  <p style="font-size: 16px;"><strong>Password:</strong> ${formData?.password}</p>
                  ${!isClientTeam ? `<p style="font-size: 16px;"><strong>Role:</strong> ${getDisplayRole(formData?.role, formData?.employeerole)}</p>` : ""}

                  <p style={{ fontSize: "14px", color: "#666" }}>
                    You can view the ProTeams
                    <a
                      href="https://proteams.io/privacy-policy/"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#1a73e8 !important", textDecoration: "underline" }}
                    >
                      Privacy Policy
                    </a> here.
                  </p>
      
                <hr style="border:none; border-top:1px solid #e5e7eb; margin:30px 0;">

                <p style="text-align:center; font-size:13px; color:#666; margin:0;">
                  For assistance, feel free to <a href="mailto:support@proteams.io" style="color:#1a73e8; text-decoration:underline;">contact support</a>.<br>
                  © ${new Date().getFullYear()} ProTeams. All rights reserved.
                </p>
              </div>
              </div>
            `,
        };
        const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };

        const promises = [
          axiosInstance.post(
            `${BaseUrl}updateUserPass`,
            { email: formData?.email, password: formData?.password },
            { headers: { ...defaultHeaders, ...authHeaders } }
          ),

          await axiosInstance.post(
            `${BaseUrl}sendEmailData`,
            {
              email: msg?.to,
              subject: msg?.subject,
              contenttxt: msg?.html,
            },
            {
              headers: {
                "Content-Type": "application/json",
                withCredentials: true,
                ...authHeaders,
              },
            }
          ),
        ];
        Promise.allSettled(promises)
          .then((results) => {
            console.log("role change results:", results);
          })
          .catch((err) => {
            console.error("role change error:", err);
          });
      };

      let newEmail = null;
      const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
      
      if (formData?.email && existingUser?.email && existingUser?.email !== formData?.email) {
        const oldEmail = existingUser?.email;
        newEmail = formData?.email;
        const displayName = `${formData?.firstName || existingUser?.firstName} ${formData?.lastName || existingUser?.lastName}`;
        const userId = id;

      if (formData) formData.email = oldEmail;
        if (formData?.employeerole === EMP_ROLES.CLIENT_TEAM) {
          await axiosInstance.post(`${BaseUrl}employeeEmailVerifyLink`, {
            oldEmail,
            newEmail,
            displayName,
            userId,
          }, { headers: { ...defaultHeaders, ...authHeaders }});
        } else {
          await axiosInstance.post(`${BaseUrl}emailVerifyLink`, {
            oldEmail,
            newEmail,
            displayName,
            userId,
          }, { headers: { ...defaultHeaders, ...authHeaders }});
        }
          // await axiosInstance.post(`${BaseUrl}updateUserAuthEmail`,
          // {
          //   userId: id,
          //   newEmail: formData?.email,
          // }, {
          //   headers: { ...defaultHeaders, ...authHeaders },
          // });
      }

      const updateData = {
        ...formData,
        ...(newEmail !== null ? { pendingEmail: newEmail } : {}),
        updatedBy: currentUserId,
        updatedAt: serverTimestamp(),
      };

      await setDoc(userDocRef, updateData, { merge: true });
      return { status: true, data: { ...formData, id } };
    }

    const emailQuery = query(
      collection(firebase, COLLECTIONS.USERS),
      where("email", "==", formData?.email),
      where("status", "==", true),
      limit(1)
    );

    const phoneQuery = query(
      collection(firebase, COLLECTIONS.USERS),
      where("phoneNumber", "==", formData?.phoneNumber),
      where("status", "==", true),
      limit(1)
    );

    const [emailSnapshot, phoneSnapshot] = await Promise.all([
      getDocs(emailQuery),
      getDocs(phoneQuery),
    ]);

    if (!emailSnapshot.empty) {
      return {
        status: false,
        field: "email",
        message: "A user with this email already exists.",
      };
    }

    if (!phoneSnapshot.empty) {
      return {
        status: false,
        field: "phone",
        message: "A user with this phone number already exists.",
      };
    }

    const newCompanyData = {
      firstname: formData.firstName,
      lastname: formData.lastName,
      email: formData.email,
      phoneNumber: formData.phoneNumber,
      password: formData.password,
    };

    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };

    const CreatedUser = await axiosInstance.post(
      `${BaseUrl}createUser`,
      newCompanyData,
      { headers: { ...defaultHeaders, ...authHeaders } }
    );

    if (CreatedUser?.data?.status === true) {
      const data = {
        ...formData,
        role: formData.role || "",
        userId: CreatedUser?.data?.uid,
        firstName: formData?.firstName || "",
        lastName: formData?.lastName || "",
        email: formData?.email || "",
        userName: formData?.email || "",
        phoneNumber: formData?.phoneNumber || "",
        password: formData?.password || "",
        status: true,
        ...(formData.role === "admin" && { companyId: formData.companyId }),
      };

      const create_user_res = await createUsers(data);
      if (create_user_res?.status === true) {
        newData = create_user_res;
        sendWelcomeEmailInBackground(formData);
        return { status: true, data: newData };
      } else {
        throw new Error(create_user_res.error || "Unknown error");
      }
    }

    return { status: false, message: "Failed to create user." };
  } catch (error) {
    console.error("saveOrUpdateUserData Error:", error);
    return {
      status: false,
      message: error.message || "An unknown error occurred",
    };
  }
}

function sendWelcomeEmailInBackground(formData) {
  const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
  const msg = {
    to: formData?.email,
    subject: `Your ProTeams account was successfully created`,
    html: `
      <div style="font-family: Arial, sans-serif; color: #333; max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 10px; overflow: hidden;">
            <div style="background-color:#f4f6f8; padding:20px; text-align:center;">
              <img
                src="https://${DOMAINURL}/csmGrayTextLogin.png"
                alt="ProTeams Logo"
                width="180"
                style="display:block; margin:0 auto; max-width:180px; width:100%; height:auto; border:0;"
              />
            </div>

            <div style="padding:25px 20px;">
          <h2 style="color: #078037;">Welcome to ProTeams!</h2>
          <p style="font-size: 16px;"><strong>Hello ${formData?.firstName},</strong></p>
          <p style="font-size: 15px;">
              Welcome to the <strong>ProTeams</strong> Web Portal. You can access the web platform using the credentials below:
          </p>
          <p style="text-align: center;">
            <a href="https://${DOMAINURL}"
              style="display: inline-block; background-color: #078037; color: white; text-decoration: none; padding: 10px 20px; border-radius: 5px; font-weight: bold;">
              Go to ProTeams Web Portal
            </a>
          </p>
          <hr style="margin: 20px 0;">
          <p style="font-size: 15px;">Here are your login credentials:</p>
          <p style="font-size: 16px;"><strong>Username:</strong> ${formData?.email}</p>
          <p style="font-size: 16px;"><strong>Password:</strong> ${formData?.password}</p>
          <hr style="border:none; border-top:1px solid #e5e7eb; margin:30px 0;">

              <p style="text-align:center; font-size:13px; color:#666; margin:0;">
                For assistance, feel free to <a href="mailto:support@proteams.io" style="color:#1a73e8; text-decoration:underline;">contact support</a>.<br>
                © ${new Date().getFullYear()} ProTeams. All rights reserved.
              </p>
      </div>
      </div>
    `,
  };

  axiosInstance
    .post(`${BaseUrl}sendEmailData`, 
      {
        email: msg?.to,
        subject: msg?.subject,
        contenttxt: msg?.html,
      }, {
      headers: {
        "Content-Type": "application/json",
        withCredentials: true,
        ...authHeaders,
      },
    })
    .then((res) => {
      console.log("Email sent:", res.data);
    })
    .catch((err) => {
      console.error("Error sending background email:", err);
    });
}

export async function deleteUsers(id) {
  try {
    const auth = getAuth();
    const currentUserId = auth.currentUser?.uid;
    const userDocRef = doc(firebase, COLLECTIONS.USERS, id);
    const userSnapshot = await getDoc(userDocRef);
    const firstName = userSnapshot.data()?.firstName || "";
    const email = userSnapshot.data()?.email || "";

    if (!currentUserId) {
      throw new Error("No authenticated user found.");
    }

    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    const res = await axiosInstance.post(
      `${BaseUrl}removeUser`,
      { id: id, firstName, email },
      { headers: { ...defaultHeaders, ...authHeaders } }
    );

    if (!res.data.status) {
      throw new Error("Failed to delete user from Firebase Authentication");
    }

    const updateData = {
      deleted: true,
      status: false,
      updatedBy: currentUserId,
      deletedBy: currentUserId,
      deletedAt: serverTimestamp(),
    };

    await updateDoc(userDocRef, updateData);

    // await updateStripeQuantity(subscriptionId, subscriptionItemId, formData.companyId);
    return { status: true };
  } catch (error) {
    return {
      status: false,
      error:
        error.message || "An unknown error occurred while deleting the user.",
    };
  }
}

export async function deleteCompanyUserAccount(email) {
  try {
    const userRef = collection(firebase, COLLECTIONS.USERS);
    const q = query(userRef, where("email", "==", email));
    const snap = await getDocs(q);

    if (snap.empty) {
      throw new Error("User not found");
    }

    const userDoc = snap.docs[0];
    const userId = userDoc.id;
    const userData = userDoc.data();

    const {firstName, customer } = userData;

    const res = await axiosInstance.post(`${BaseUrl}removeUser`, {
      id: userId,
      firstName,
      email,
      update: true
    });

    if (!res.data.status) {
      throw new Error(res.data.message || "Failed to delete user");
    }

    if (customer) {
      await stripe.customers.del(customer);
    }

    return { status: true };
  } catch (error) {
    console.error("RemoveCompanyUserAccount error:", error);
    return { status: false, error: error.message };
  }
}

export async function createStripeLink(id, subscriptionId) {
  try {
    if (!id) {
      throw new Error("Customer ID is required.");
    }

    const session = await stripe.billingPortal.sessions.create({
      customer: id,
      return_url: `https://${DOMAINURL}`,
      flow_data: {
        type: "subscription_cancel",
        subscription_cancel: {
          subscription: subscriptionId,
        },
      },
    });

    return { url: session?.url };
  } catch (error) {
    console.error("Error creating customer portal session:", error);
    return { url: null };
  }
}

export async function createUpdatePlanStripeLink(id, subscriptionId) {
  try {
    if (!id) {
      throw new Error("Customer ID is required.");
    }

    const session = await stripe.billingPortal.sessions.create({
      customer: id,
      return_url: `https://${DOMAINURL}`,
      flow_data: {
        type: "subscription_update",
        subscription_update: {
          subscription: subscriptionId,
        },
      },
    });

    return { url: session?.url };
  } catch (error) {
    console.error("Error creating customer portal session:", error);
    return { url: null };
  }
}

export async function CreateUpdateLink(id) {
  try {
    if (!id) {
      throw new Error("Customer ID is required.");
    }

    const session = await stripe.checkout.sessions.create({
      customer: id,
      mode: "subscription",
      line_items: [
        {
          price: "price_1R4GDLH54HzvHwSdHlTrEWAF",
          quantity: 1,
          adjustable_quantity: {
            enabled: true,
            minimum: 0,
            maximum: 100,
          },
        },
      ],
      success_url: `https://${DOMAINURL}`,
    });

    return { url: session?.url };
  } catch (error) {
    console.error("Error creating customer portal session:", error);
    return { url: null };
  }
}

export async function getStripeConfigs() {
  try {
    const authHeaders = {
      Authorization: `Bearer ${getLoginToken()}`,
    };
    const res = await axiosInstance.get(`${BaseUrl}getStripeConfig`, {
      headers: {
        "Content-Type": "application/json",
        withCredentials: true,
        ...authHeaders,
      },
    });

    return {
      success: true,
      status: res.status,
      data: res.data.data,
    };
  } catch (error) {
    console.error("Error fetching Stripe config:", error);
    return {
      success: false,
      status: error.response?.status || 500,
      error: error.response?.data?.message || "Failed to fetch config",
    };
  }
}

export async function CreateStripeSubscription(email, priceId) {
  try {
    const authHeaders = {
      Authorization: `Bearer ${getLoginToken()}`,
    };

    const res = await axiosInstance.post(
      `${BaseUrl}createSubscription`,
      {
        email,
        priceId,
      },
      {
        headers: {
          "Content-Type": "application/json",
          withCredentials: true,
          ...authHeaders,
        },
      }
    );

    return {
      success: true,
      status: res.status,
      data: res.data.data,
    };
  } catch (error) {
    console.error("Error creating Stripe subscription:", error);
    return {
      success: false,
      status: error.response?.status || 500,
      error: error.response?.data?.message || "Failed to create subscription",
    };
  }
}

export async function createNewSubscriptionAfterCancel(
  customerId,
  newPriceId,
  promoCode,
) {
  try {
    const authHeaders = {
      Authorization: `Bearer ${getLoginToken()}`,
    };

    const res = await axiosInstance.post(
      `${BaseUrl}createNewSubscription`,
      {
        customerId,
        newPriceId,
        promoCode,
      },
      {
        headers: {
          "Content-Type": "application/json",
          withCredentials: true,
          ...authHeaders,
        },
      }
    );

    return {
      success: true,
      status: res.status,
      data: res?.data?.data,
    };
  } catch (error) {
    console.error("Error updating Stripe subscription:", error);
    return {
      success: false,
      status: error.response?.status || 500,
      error: error.response?.data?.message || "Failed to update subscription",
    };
  }
}

export async function gettheSetupIntent(
  customerId
) {
  try {
    const authHeaders = {
      Authorization: `Bearer ${getLoginToken()}`,
    };

    const res = await axiosInstance.post(
      `${BaseUrl}createSetupIntent`,
      {
        customerId,
      },
      {
        headers: {
          "Content-Type": "application/json",
          withCredentials: true,
          ...authHeaders,
        },
      }
    );

    return {
      success: true,
      status: res.status,
      data: res?.data,
    };
  } catch (error) {
    console.error("Error updating Stripe subscription:", error);
    return {
      success: false,
      status: error.response?.status || 500,
      error: error.response?.data?.message || "Failed to update subscription",
    };
  }
}

export async function getCurrentPhase(subscriptionId) {
  const schedule = await stripe.subscriptionSchedules.retrieve(
    subscriptionId
  );

  const now = Math.floor(Date.now() / 1000);

  const currentPhaseIndex = schedule.phases.findIndex(phase => {
    return (
      phase.start_date <= now &&
      (!phase.end_date || phase.end_date > now)
    );
  });

  return {
    currentPhaseIndex,
    phases: schedule.phases
  };
}


export async function UpdateStripeScheduleSubscription(
  subscriptionId,
  selectedProductId,
  normalPrice
) {
  try {
    const authHeaders = {
      Authorization: `Bearer ${getLoginToken()}`,
    };

    const res = await axiosInstance.post(
      `${BaseUrl}updateSubscriptionSchedulePlan`,
      {
        subscriptionId,
        selectedProductId,
        normalPrice,
      },
      {
        headers: {
          "Content-Type": "application/json",
          withCredentials: true,
          ...authHeaders,
        },
      }
    );

    return {
      success: true,
      status: res.status,
    };
  } catch (error) {
    console.error("Error updating Stripe subscription:", error);
    return {
      success: false,
      status: error.response?.status || 500,
      error: error.response?.data?.message || "Failed to update subscription",
    };
  }
}

export async function UpgradeStripeSubscription(
  subscriptionId,
  newPriceId,
  promoCode,
  paymentMethodId
) {
  try {
    const authHeaders = {
      Authorization: `Bearer ${getLoginToken()}`,
    };

    const res = await axiosInstance.post(
      `${BaseUrl}updateSubscription`,
      {
        subscriptionId,
        newPriceId,
        promoCode,
        paymentMethodId
      },
      {
        headers: {
          "Content-Type": "application/json",
          withCredentials: true,
          ...authHeaders,
        },
      }
    );

    return {
      success: true,
      status: res.status,
      downgrade: res?.data?.downgrade,
      data: res?.data?.data,
      switch: res?.data?.switch,
    };
  } catch (error) {
    console.error("Error updating Stripe subscription:", error);
    return {
      success: false,
      status: error.response?.status || 500,
      error: error.response?.data?.message || "Failed to update subscription",
    };
  }
}

export async function UpgradeQuantitySubscription(
  customerId,
  newPriceId,
  promoCode,
  quantity
) {
  try {
    const authHeaders = {
      Authorization: `Bearer ${getLoginToken()}`,
    };

    const res = await axiosInstance.post(
      `${BaseUrl}updateQuantitySubscription`,
      {
        customerId,
        newPriceId,
        promoCode,
        quantity,
      },
      {
        headers: {
          "Content-Type": "application/json",
          withCredentials: true,
          ...authHeaders,
        },
      }
    );

    return {
      success: true,
      status: res.status,
      data: res?.data?.data,
    };
  } catch (error) {
    console.error("Error updating Stripe subscription:", error);
    return {
      success: false,
      status: error.response?.status || 500,
      error: error.response?.data?.message || "Failed to update subscription",
    };
  }
}

export async function UpgradeNewQuantitySubscription(
  subscriptionId,
  newPriceId,
  promoCode,
  quantity
) {
  try {
    const authHeaders = {
      Authorization: `Bearer ${getLoginToken()}`,
    };

    const res = await axiosInstance.post(
      `${BaseUrl}updateNewQuantitySubscription`,
      {
        subscriptionId,
        newPriceId,
        promoCode,
        quantity,
      },
      {
        headers: {
          "Content-Type": "application/json",
          withCredentials: true,
          ...authHeaders,
        },
      }
    );

    return {
      success: true,
      status: res.status,
      data: res?.data?.data,
    };
  } catch (error) {
    console.error("Error updating Stripe subscription:", error);
    return {
      success: false,
      status: error.response?.status || 500,
      error: error.response?.data?.message || "Failed to update subscription",
    };
  }
}

export async function calculateSubscriptionPayment(subscriptionId, newPriceId, promoCode) {
  const subscriptionPromise = stripe.subscriptions.retrieve(subscriptionId);
  const newPricePromise = stripe.prices.retrieve(newPriceId);

  const [subscription, newPrice] = await Promise.all([subscriptionPromise, newPricePromise]);

  const customerId = subscription.customer;
  const currentPriceAmount = subscription.items.data[0].price.unit_amount;

  const customer = await stripe.customers.retrieve(customerId);
  const currentBalance = customer.balance || 0;

  let discountAmount = 0;
  let promoId;
  if (promoCode) {
    const promotionCodes = await stripe.promotionCodes.list({ code: promoCode, active: true });
    if (promotionCodes.data.length > 0) {
      const promo = promotionCodes.data[0];
      promoId = promo.id;
      if (promo.coupon.amount_off) discountAmount = promo.coupon.amount_off;
      else if (promo.coupon.percent_off) discountAmount = (promo.coupon.percent_off / 100) * newPrice.unit_amount;
    }
  }

  const adjustedNewPrice = newPrice.unit_amount - discountAmount;

  const SECONDS_IN_A_DAY = 60 * 60 * 24;
  const planInterval = subscription.items.data[0].plan.interval;
  const totalPeriodDays = planInterval === 'month'
    ? 30
    : Math.ceil((subscription.current_period_end - subscription.current_period_start) / SECONDS_IN_A_DAY);

  const secondsUsed = Date.now() / 1000 - subscription.current_period_start;
  const daysUsed = Math.floor(secondsUsed / SECONDS_IN_A_DAY);
  const daysRemaining = totalPeriodDays - daysUsed;

  const oldDailyRate = currentPriceAmount / totalPeriodDays;
  const creditFromOldPlan = oldDailyRate * daysRemaining;

  const finalAmountDue = Math.round(adjustedNewPrice - creditFromOldPlan + currentBalance);

  return {
    finalAmountDue,
    creditFromOldPlan,
    adjustedNewPrice,
    daysUsed,
    daysRemaining,
    promoApplied: Boolean(promoId),
    promoId,
    isUpgrade: newPrice.unit_amount > currentPriceAmount,
  };
}


export async function PaymentFailedEmail(formData, paymentMethod) {
  const dateTime = moment().format("MM/DD/YY");
  let emailContent = "";

  if (paymentMethod === "ach") {
    emailContent = `
      <p>Hello ${formData.name},</p>

      <p>Unfortunately, this bank transaction has failed.</p>

      <p>Please check your bank information and try again. 
      If the payment continues to fail, please contact your banking institution for assistance.</p>

      <p>If you have any questions or need help, feel free to reply to this email — we're here to support you.</p>

      <p>Thank you,<br/>ProTeams Billing Team</p>
    `;
  }

  else {
    emailContent = `
      <p>Hello ${formData.name},</p>

      <p>We were unable to process your recent payment for your ProTeams subscription.</p>

      <p>Please double-check your card details and try the payment again. 
      If the issue persists, consider using a different card or contacting your card provider for assistance.</p>

      <p>If you need any help, simply reply to this email — our team is always here to support you.</p>

      <p>Thank you,<br/>The ProTeams Billing Team</p>
    `;
  }

  const { subject, content } = EmailTemplate({
    subject: `ProTeams - (${formData.companyName}) UnSuccessful Payment (${dateTime})`,
    content: emailContent,
  });
  try {
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    axiosInstance.post(
      `${BaseUrl}sendEmailData`,
      {
        email: formData?.email,
        subject: subject,
        contenttxt: content,
      },
      {
        headers: {
          "Content-Type": "application/json",
          withCredentials: true,
          ...authHeaders,
        },
      }
    );

    return { success: true };
  } catch (error) {
    console.error("Error sending payment failed email:", error);
    return { success: false, error };
  }
}

export async function SuccesDownGradeEmail(formData, data, subscriptionId) {
  try {
    const subscription = await stripe.subscriptions.retrieve(subscriptionId);
    const product = await stripe.products.retrieve(
      subscription.items.data[0].price.product
    );
    const planName = product.name;

    const {
      currentBalanceAfterCredit,
      discountApplied,
      discountAmount,
    } = data;

    const formatStripeAmount = (amount) => {
      return `$${(amount / 100).toFixed(2)}`;
    };

    const { subject, content } = EmailTemplate({
      subject: `Your ${formData?.companyName}'s Subscription has Been Downgraded`,
      content: `
        <p>Hello ${formData?.name},</p>

        <p>We’re writing to confirm that your subscription has been successfully downgraded to the <strong>${planName}</strong> plan. This change has been applied, and future billing will reflect this updated package.</p>

        ${
          currentBalanceAfterCredit > 0
            ? `<p><strong>Account Credit:</strong> ${formatStripeAmount(currentBalanceAfterCredit)} has been applied to your account. This credit will automatically be used toward your upcoming billing cycle.</p>`
            : ""
        }

        <p>If you have any questions or need further assistance, please don’t hesitate to reach out. We're here to help!</p>

        <p>Thank you for choosing <strong>ProTeams</strong>. We value your continued support.</p>

        <p>Best regards,<br/>The ProTeams Billing Team</p>
      `,
    });

    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    axiosInstance.post(
      `${BaseUrl}sendEmailData`,
      {
        email: formData?.email,
        subject: subject,
        contenttxt: content,
      },
      {
        headers: {
          "Content-Type": "application/json",
          withCredentials: true,
          ...authHeaders,
        },
      }
    );

    return { success: true };
  } catch (error) {
    console.error("Error sending downgrade confirmation email:", error);
    return { success: false, error };
  }
}

export async function SuccesUpdateSubscriptionEmail(formData) {
  try {
    const { subject, content } = EmailTemplate({
      subject: "Your Subscription Has Been Changed",
      content: `
     <p>Hello ${formData?.name},</p>

      <p>We're writing to confirm that your subscription has been successfully updated. Your changes have been applied, and there is no additional payment required at this time.</p>

      <p>You can continue to enjoy the updated benefits of your plan without any interruption. Your billing will reflect the new subscription details going forward.</p>

      <p>If you have any questions or need assistance with your account, please don't hesitate to reach out to our support team.</p>

      <p>Thank you for choosing ProTeams We appreciate your continued trust in our services.</p>

      <p>Best regards,<br/>The ProTeams Support Team</p>
    `,
    });
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    axiosInstance.post(
      `${BaseUrl}sendEmailData`,
      {
        email: formData?.email,
        subject: subject,
        contenttxt: content,
      },
      {
        headers: {
          "Content-Type": "application/json",
          withCredentials: true,
          ...authHeaders,
        },
      }
    );

    return { success: true };
  } catch (error) {
    console.error("Error sending payment failed email:", error);
    return { success: false, error };
  }
}

export async function sendEmailForAchPaymentDetail(
  newPriceId,
  customerId,
  newAmounts
) {
  const colRef = collection(firebase, COLLECTIONS.USERS);
  const q = query(colRef, where("customer", "==", customerId));
  const customerQuerySnapshot = await getDocs(q);
  const userData = customerQuerySnapshot.docs[0]?.data();

  const customerEmail = userData?.email;
  const companyName = `${userData?.companyName}`;
  const customerName = userData?.firstName;
  const dateTime = moment().format("MM/DD/YY");

  const price = await stripe.prices.retrieve(newPriceId);

  const product = await stripe.products.retrieve(price.product);

  const planName = product.name;
  const currentAmount = price.unit_amount / 100;
  const newPaymentAmount = newAmounts / 100;

  const content = `
    <p>Hi ${customerName},</p>

    <p>Your ACH payment request for the <strong>${planName}</strong> subscription has been received and is now being processed.</p>

    <p>Below are the payment details:</p>
    <ul>
      <li><strong>Plan:</strong> ${planName}</li>
      <li><strong>New Plan Amount:</strong> $${currentAmount.toFixed(2)}</li>
      <li><strong>Total Payable Amount (after discount):</strong> $${newPaymentAmount.toFixed(2)}</li>
    </ul>

    <p>Please note:</p>
    <p>ACH transactions typically take <strong>few business days</strong> to complete. Once the payment is successfully processed, your subscription status and billing details will automatically update in the system.</p>

    <p>If you have any questions or need assistance, feel free to reach out.</p>

    <p>Best regards,<br/>
    The ProTeams Team</p>
  `;

  const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };

  const { subject: emailSubject, content: emailContent } = EmailTemplate({
    subject: `ProTeams – (${companyName}) ACH Payment Processing (${dateTime})`,
    headline: `ACH Payment Processing`,
    content,
  });

  await axiosInstance.post(
    `${BaseUrl}sendEmailData`,
    {
      email: customerEmail,
      subject: emailSubject,
      contenttxt: emailContent,
    },
    {
      headers: {
        "Content-Type": "application/json",
        withCredentials: true,
        ...authHeaders,
      },
    }
  );
}


export async function finalizeFreeSubscription(
  cardDetails,
  formData,
  subscriptionId,
  newPriceId,
  newProduct,
  setupIntentId,
  paymentMethodType,
  code,
  userId
) {
  try {

    const cardCollectionRef = collection(firebase, COLLECTIONS.STRIPE_CARDS);
    await addDoc(cardCollectionRef, {
      card: cardDetails,
      createdAt: new Date(),
    });

    const userDocRef = doc(firebase, COLLECTIONS.USERS, userId);
    await setDoc(userDocRef, {
      userPromoCode: code,
    }, { merge: true });

    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };

    const setupIntent = await stripe.setupIntents.retrieve(setupIntentId);

    if (setupIntent.status !== "succeeded") {
      console.error("SetupIntent not successful");
    }

    const paymentMethodId = setupIntent.payment_method;
    const customerId = setupIntent.customer;

    await stripe.customers.update(customerId, {
      invoice_settings: {
        default_payment_method: paymentMethodId,
      },
      balance: 0,
    });

    await axiosInstance.post(
      `${BaseUrl}updateSubscriptionSchedulePlan`,
      {
        subscriptionId: subscriptionId,
        selectedProductId: newProduct,
        normalPrice: newPriceId,
      },
      {
        headers: {
          "Content-Type": "application/json",
          withCredentials: true,
          ...authHeaders,
        },
      }
    )

    const commonHtml = 
    `<h3> Welcome, ${formData?.name}</h3>
      <p>
      Thank you for partnering with us.
      </p>

      <p>
      As part of your partnership benefits, you’ve been enrolled in <strong>6 months of complimentary access</strong> to our platform.
      </p>

      <p>
      <strong>You will not be charged during this free period.</strong>
      </p>

      <p>
      We’ve securely saved your payment details on file for future billing reference once your complimentary access ends.
      </p>

      <h3>What This Means</h3>

      <p>• You have full access for 6 months at no cost.</p>
      <p>• No payment has been processed.</p>
      <p>• Billing will begin only after your free period ends.</p>
      <p>• You will receive a notification before any future charge.</p>`
    

      const achBlock = 
      `<h3>Bank Account Verification Required</h3>
      <p>
      Since you chose to add a bank account (ACH), a quick verification step is required.
      </p>

      <p>
      Stripe will send two small micro-deposits to your bank account within 1–2 business days.
      </p>

      <p>
      Once you receive them:
      </p>

      <p>• Log in to your account.</p>
      <p>• Enter the deposit amounts to verify your bank account.</p>

      <p>
      No charges will occur during this verification process.
      </p>
      `

      const closingBlock = 
      `<p>
        Your payment information is securely encrypted and processed by Stripe, a certified PCI-compliant payment provider.
        </p>

        <p>
        If you need to update your payment method at any time, you can do so from your account settings.
        </p>

        <p>
        We’re excited to have you as a partner and look forward to growing together.
        </p>

        <p>
        Best regards,<br/>
        <strong>ProTeams Team</strong>
        </p>
        `
      let emailBody = commonHtml;

      if (paymentMethodType === "ach") {
        emailBody += achBlock;
      }

      emailBody += closingBlock;

    const { subject: emailSubject, content: emailContent } =
      EmailTemplate({
        subject: "Welcome Partner – Your Free Access Has Been Activated",
        content: emailBody,
      });

    axiosInstance.post(
      `${BaseUrl}sendEmailData`,
      {
        email: formData.email,
        subject: emailSubject,
        contenttxt: emailContent,
      },
      {
        headers: {
          "Content-Type": "application/json",
          withCredentials: true,
          ...authHeaders,
        },
      }
    ).catch(err => {
      console.error("Background email error:", err?.response?.data || err.message);
    });


    return { success: true };

  } catch (error) {
    console.error("Error finalizing free subscription:", error);
    return { success: false };
  }
}


export async function saveCardDetails(
  cardDetails,
  formData,
  subscriptionId,
  newPriceId,
  newAmounts,
  paymentMethodId,
  paymentMethodType,
  code,
  userId
) {
  try {
    const cardCollectionRef = collection(firebase, COLLECTIONS.STRIPE_CARDS);
    await addDoc(cardCollectionRef, {
      card: cardDetails,
      createdAt: new Date(),
    });

    const userDocRef = doc(firebase, COLLECTIONS.USERS, userId);
    await setDoc(userDocRef, {
      userPromoCode: code,
    }, { merge: true });

    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };

    const subscriptionBeforeUpdate = await stripe.subscriptions.retrieve(
      subscriptionId
    );
    const oldStatus = subscriptionBeforeUpdate.status;
    const currentItemId = subscriptionBeforeUpdate.items.data[0].id;
    const customerId = subscriptionBeforeUpdate.customer;
    const PriceIds = [
      "price_1R1Pm6H54HzvHwSdzS4aA0s9",
      "price_1RGH20H54HzvHwSdZTHWjNHO",
    ];

    const customer = await stripe.customers.retrieve(customerId);
    const currentDefault = customer?.invoice_settings?.default_payment_method;

    if (paymentMethodType === "ach") {
      // await stripe.paymentMethods.attach(paymentMethodId, { customer: customerId });

      // if (!currentDefault) {
      //   await stripe.customers.update(customerId, {
      //     invoice_settings: { default_payment_method: paymentMethodId }
      //   });
      // }

      // await stripe.subscriptions.update(subscriptionId, {
      //   items: [{ id: currentItemId, price: newPriceId }],
      //   proration_behavior: "none",
      //   billing_cycle_anchor: "now",
      //   trial_end: "now",
      // });

      return { success: true };
    } else {
      await stripe.paymentMethods.attach(paymentMethodId, { customer: customerId });
    }

    if (!currentDefault) {
      await stripe.customers.update(customerId, {
        invoice_settings: {
          default_payment_method: paymentMethodId,
        },
      balance: 0,
      });
    }

    if (PriceIds.includes(newPriceId)) {
      console.log("Price match found. Skipping subscription update.");
    } else {
      if (oldStatus === "trialing") {
        await stripe.subscriptions.update(subscriptionId, {
          trial_end: "now",
          proration_behavior: "none",
          billing_cycle_anchor: 'now',
          items: [{ id: currentItemId, price: newPriceId }],
        });
      } else if (oldStatus !== "trialing" && oldStatus !== "canceled") {
        await stripe.subscriptions.update(subscriptionId, {
          items: [{ id: currentItemId, price: newPriceId }],
          proration_behavior: "none",
          billing_cycle_anchor: 'now',
        });
      }
    }
    const subscription = await stripe.subscriptions.retrieve(subscriptionId);
    const colRef = collection(firebase, COLLECTIONS.USERS);

    const q = query(colRef, where("customer", "==", customerId));

    const customerQuerySnapshot = await getDocs(q);
    const userData = customerQuerySnapshot.docs[0]?.data();
    const customerEmail = userData?.email;
    const companyName = `${userData?.companyName}`;
    const dateTime = moment().format("MM/DD/YY");

    const customerName = userData?.firstName;

    const product = await stripe.products.retrieve(
      subscription.items.data[0].price.product
    );
    const planName = product.name;
    const paymentAmount = subscription.items.data[0].price.unit_amount / 100;
    const paymentNewAmount = newAmounts / 100;
    const nextPaymentDate = new Date(subscription.current_period_end * 1000);
    const formattedNextPaymentDate = `${
      nextPaymentDate.getMonth() + 1
    }/${nextPaymentDate.getDate()}/${nextPaymentDate.getFullYear()}`;

    const emailPromises = [];

    if (["trialing", "canceled"].includes(oldStatus)) {
      const subject =
        oldStatus === "trialing"
          ? `ProTeams - (${companyName}) Subscription Activated (${dateTime})`
          : `ProTeams - (${companyName}) Subscription Renewed (${dateTime})`;

      const content = `
        <p>Hi ${customerName},</p>
    
        <p>${
          oldStatus === "trialing"
            ? "We're excited to let you know that your free trial has been successfully upgraded to the"
            : "We're pleased to inform you that your subscription to the"
        } <strong>${planName}</strong> plan is now active.</p>
    
        <p>Here are your subscription details:</p>
        <ul>
          <li><strong>Plan:</strong> ${planName}</li>
          <li><strong>Payment Amount:</strong> $${paymentAmount.toFixed(2)}</li>
          <li><strong>Next Billing Date:</strong> ${formattedNextPaymentDate}</li>
        </ul>
    
        <p>You can manage your subscription, update payment details, or make changes at any time by logging into your ProTeams account.</p>
    
        <p>Thank you for choosing ProTeams—we’re excited to support your team’s success.</p>
    
        <p>Best regards,<br/>
        The ProTeams Team</p>
    
        <p>
          <a href="mailto:support@proteams.io">support@proteams.io</a> |
          <a href="https://www.proteams.io">www.proteams.io</a>
        </p>
      `;

    } else if (oldStatus === "active"){
      const subject = `ProTeams - (${companyName}) Subscription Activated (${dateTime})`;

      const content = `
        <p>Hi ${customerName},</p>
    
        <p>${"We're pleased to inform you that your subscription to the"} <strong>${planName}</strong> plan is now active.</p>
    
        <p>Here are your subscription details:</p>
        <ul>
          <li><strong>Plan:</strong> ${planName}</li>
          <li><strong>Plan Amount:</strong> $${paymentAmount.toFixed(2)}</li>
          <li><strong>Total Payable Amount (after discount):</strong> $${paymentNewAmount.toFixed(2)}</li>
        </ul>
    
        <p>You can manage your subscription, update payment details, or make changes at any time by logging into your ProTeams account.</p>
    
        <p>Thank you for choosing ProTeams—we’re excited to support your team’s success.</p>
    
        <p>Best regards,<br/>
        The ProTeams Team</p>
    
        <p>
          <a href="mailto:support@proteams.io">support@proteams.io</a> |
          <a href="https://www.proteams.io">www.proteams.io</a>
        </p>
      `;

      const { subject: emailSubject, content: emailContent } = EmailTemplate({
        subject,
        headline: subject,
        content,
      });

      emailPromises.push(
        axiosInstance.post(
          `${BaseUrl}sendEmailData`,
          {
            email: customerEmail,
            subject: emailSubject,
            contenttxt: emailContent,
          },
          {
            headers: {
              "Content-Type": "application/json",
              withCredentials: true,
              ...authHeaders,
            },
          }
        )
      );
    }

    const paymentSubject = `ProTeams - (${companyName}) Successful Payment (${dateTime})`;
    const paymentContent = `
      <p>Dear ${formData.name},</p>

    <p>We are pleased to inform you that we have successfully processed your payment, and your ProTeams subscription has been updated accordingly.</p>

    <p>Thank you for being an essential part of the ProTeams community. We’re excited to continue supporting your operations and help you achieve even more success.</p>

    <p>If you have any questions or need further assistance, please don't hesitate to reach out.</p>

    <p>Best regards, <br>The ProTeams Billing Team</p>

`;

    const { subject: paymentEmailSubject, content: paymentEmailContent } =
      EmailTemplate({
        subject: paymentSubject,
        headline: paymentSubject,
        content: paymentContent,
      });

    emailPromises.push(
      axiosInstance.post(
        `${BaseUrl}sendEmailData`,
        {
          email: formData.email,
          subject: paymentEmailSubject,
          contenttxt: paymentEmailContent,
        },
        {
          headers: {
            "Content-Type": "application/json",
            withCredentials: true,
            ...authHeaders,
          },
        }
      )
    );

    await Promise.all(emailPromises);

    return { success: true };
  } catch (error) {
    console.error("Error saving card details:", error);
    return { success: false, error };
  }
}

export async function findSubscriptionByCustomerId(customerId) {
  try {
    const subsRef = collection(firebase, COLLECTIONS.STRIPE_SUBSCRIPTION);

    const q = customerId
      ? query(subsRef, where("customer", "==", customerId))
      : query(subsRef);

    const querySnapshot = await getDocs(q);

    const subscriptions = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return { status: "success", data: subscriptions };
  } catch (error) {
    console.error("Error finding subscriptions:", error);
    return { status: "error", message: error.message };
  }
}
export async function fetchSubscriptionWithCustomer(customerId) {
  try {
    if (!customerId) {
      return { status: "success", data: [] };
    }

    const subsRef = collection(firebase, COLLECTIONS.STRIPE_SUBSCRIPTION);
    const q = query(subsRef, where("customer", "==", customerId));

    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const subscriptions = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      return { status: "success", data: subscriptions };
    } else {
      return { status: "success", data: [] };
    }
  } catch (error) {
    console.error("Error finding subscriptions:", error);
    return { status: "error", message: error.message };
  }
}


export async function getCardDetails(paymentIntentId) {
  try {
    const charges = await stripe.charges.list({
      payment_intent: paymentIntentId,
      limit: 1,
    });

    const charge = charges.data[0];
    const card = charge?.payment_method_details?.card;

    if (card) {
      return {
        brand: card.brand,
        last4: card.last4,
        exp_month: card.exp_month,
        exp_year: card.exp_year,
      };
    } else {
      return null;
    }
  } catch (err) {
    console.error("Error fetching card details:", err.message);
    return null;
  }
}

export async function getBankAccountDetails (paymentIntent) {
  try {
    const details = paymentIntent?.charges?.data?.[0]?.payment_method_details;

    if (!details || details.type !== "us_bank_account") {
      console.error("Invalid ACH data");
    }

    return {
      type: "bank_account",
      bankName: details.us_bank_account.bank_name,
      last4: details.us_bank_account.last4,
      routingNumber: details.us_bank_account.routing_number,
      fingerprint: details.us_bank_account.fingerprint,
    };
  } catch (err) {
    console.error("ACH parse error:", err.message);
    return {};
  }
};

export async function CancelStripeSubscription(subscriptionId, customerId, formData){
  try {
    const authHeaders = {
      Authorization: `Bearer ${getLoginToken()}`,
    };


    const userRef = collection(firebase, COLLECTIONS.USERS);
    const q = query(userRef, where("customer", "==", customerId));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const userDoc = querySnapshot.docs[0];
      const userDocRef = userDoc.ref;

      const cancelReason = {
        feedBack: formData?.feedBack || "",
        selectedOption: formData?.selectedOption || "",
        cancelAt: serverTimestamp()
      };

      await updateDoc(userDocRef, cancelReason);
    }

    Promise.allSettled([
      axiosInstance.post(
        `${BaseUrl}cancelSubscription`,
        { subscriptionId },
        {
          headers: {
            "Content-Type": "application/json",
            withCredentials: true,
            ...authHeaders,
          },
        }
      )
    ])
      .then((results) => {
        console.log("Cloud function result:", results);
      })
      .catch((err) => {
        console.error("Cloud function error (background):", err);
      });

    return {
      success: true,
      message: "Cancellation process initiated and saved",
    };
  } catch (error) {
    console.error("Error saving cancellation reason:", error);
    return {
      success: false,
      status: error.response?.status || 500,
      error: error.response?.data?.message || "Failed to update subscription info",
    };
  }
}

export async function getAmounts(clientSecretString) {
  try {
    const paymentIntentId = clientSecretString?.split("_secret")[0];
    const paymentIntent = await stripe.paymentIntents.retrieve(paymentIntentId);
    const amount = paymentIntent.amount;
    return { amount };
  } catch (error) {
    console.error("Error retrieving payment intent:", error.message);
    return { amount: null, error: error.message };
  }
}

export async function getPromotionCodes(code) {
  try {
    const promoResponse = await stripe.promotionCodes.list({ code: code });
    if (promoResponse.data.length > 0) {
      const promo = promoResponse.data[0];
      if (promo.active) {
        if (promo.coupon.amount_off) {
          return { type: "amount", value: promo.coupon.amount_off };
        } else if (promo.coupon.percent_off) {
          return { type: "percent", value: promo.coupon.percent_off };
        }
      }
    }
    return null;
  } catch (error) {
    console.error("Error fetching promo code:", error);
    return null;
  }
}

export async function getCustomerBalanceAmount(customerId) {
  const customer = await stripe.customers.retrieve(customerId);
  return customer.balance;
}

export async function getChargesData(invoice) {
  const paymentIntentId = invoice?.payment_intent
  const paymentIntent = await stripe.paymentIntents.retrieve(paymentIntentId);
  const chargeId = paymentIntent?.latest_charge;
  const charge = await stripe.charges.retrieve(chargeId);
  return charge;
}

export async function getFreshInvoiceData (invoiceId) {
  try {
      const invoice = await stripe.invoices.retrieve(invoiceId);
      return invoice;
    } catch (error) {
      console.error("Error fetching invoice:", error);
      return { error: "Failed to fetch invoice" };
    }
};

export async function getCustomerPaymentMethods(customerId) {
  try {
    const cardsPromise = stripe.paymentMethods.list({
      customer: customerId,
    });

    const customerPromise = stripe.customers.retrieve(customerId);

    const [cards, customer] = await Promise.all([
      cardsPromise,
      customerPromise,
    ]);

    const defaultMethodId =
      customer?.invoice_settings?.default_payment_method || null;

    return {
      cards: cards.data,
      defaultPaymentMethodId: defaultMethodId,
    };
  } catch (error) {
    console.error("Error fetching payment methods:", error);
    return {
      cards: [],
      defaultPaymentMethodId: null,
    };
  }
}

export async function getPaymentMethod(customerId) {
  try {
    const cardsPromise = stripe.paymentMethods.list({
      customer: customerId,
    });

    const [cards] = await Promise.all([
      cardsPromise,
    ]);

    return {
      cards: cards.data,
    };
  } catch (error) {
    console.error("Error fetching payment methods:", error);
    return {
      cards: [],
    };
  }
}


export async function attachPaymentMethod(paymentMethodId, customerId) {
  try {
    await stripe.paymentMethods.attach(paymentMethodId, {
      customer: customerId,
    });

    const customer = await stripe.customers.retrieve(customerId);

    if (!customer.invoice_settings?.default_payment_method) {
      await stripe.customers.update(customerId, {
        invoice_settings: {
          default_payment_method: paymentMethodId,
        },
      });
    }

    return true;
  } catch (error) {
    console.error("Stripe attachPaymentMethod error:", error);
    throw error;
  }
}

export async function detachPaymentMethod(paymentMethodId) {
  try {
    const detached = await stripe.paymentMethods.detach(paymentMethodId);
    return detached;
  } catch (error) {
    console.error("Error detaching payment method:", error);
    throw error;
  }
}

export async function updateDefaultPaymentMethod(paymentMethodId, customerId) {
  try {
    await stripe.customers.update(customerId, {
      invoice_settings: {
        default_payment_method: paymentMethodId,
      },
    });
    return true;
  } catch (error) {
    console.error("Error updating default payment method:", error);
    throw error;
  }
}

export async function getBankAccountSetupIntentFromServer(customerId) {
  try {
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    const response = await axiosInstance.post(
      `${BaseUrl}createACHSetupIntent`,
      { customerId },
      {
        headers: {
          "Content-Type": "application/json",
          withCredentials: true,
          ...authHeaders,
        },
      }
    );

    const setupIntentClientSecret = response.data?.setupIntentClientSecret;

    if (!setupIntentClientSecret) {
      console.error("Missing setupIntentClientSecret from server");
    }

    return setupIntentClientSecret;
  } catch (error) {
    console.error("Error fetching SetupIntent:", error);
    throw error;
  }
}

export async function attachAndVerifyCustomerBankAccount(
  setupIntentId,
  amounts,
  docId
) {
  try {
    const verification = await stripe.setupIntents.verifyMicrodeposits(
      setupIntentId,
      { amounts }
    );

    const docRef = doc(firebase, COLLECTIONS.PAYMENT_VERIFICATION, docId);
    await updateDoc(docRef, {
      status: 'verified',
      verifiedAt: serverTimestamp(),
    });

    return {
      success: true,
      verification,
    };

  } catch (error) {
    console.error("ACH Verification Error:", error);

    return {
      success: false,
      message: error.message,
    };
  }
}

export async function attachBankAccount(paymentMethodId, customerId, userId, paymentMethod) {
  try {
    const setupIntent = await stripe.setupIntents.create({
      customer: customerId,
      payment_method_types: ["us_bank_account"],
      payment_method: paymentMethodId,
    });

    await addDoc(collection(firebase, COLLECTIONS.PAYMENT_VERIFICATION), {
      paymentMethodId,
      paymentMethod,
      setupIntentId: setupIntent.id,
      userId,
      method: "pending_verification",
      status: "pending",
      createdAt: serverTimestamp(),
    });

    return {
      success: true,
      paymentMethodId,
      clientSecret: setupIntent.client_secret,
    };

  } catch (error) {
    console.error("Error attaching bank account:", error);
    return { success: false };
  }
}

export async function exchangeStripeCodes(code) {
  try {
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };

    const response = await axiosInstance.post(
      `${BaseUrl}exchangeStripeCode`,
      { code },
      {
        headers: {
          'Content-Type': 'application/json',
          withCredentials: true,
          ...authHeaders,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching or saving Stripe data", error);
    throw error;
  }
}

export async function verifyAchMicrodeposits(intentId, amounts) {
  try {
    if (intentId.startsWith("seti_")) {
      await stripe.setupIntents.verifyMicrodeposits(
        intentId,
        { amounts }
      );
    } else {
      await stripe.paymentIntents.verifyMicrodeposits(
        intentId,
        { amounts }
      );
    }

    return true;
  } catch (error) {
    console.error("Error verifyAchMicrodeposits:", error);
    return false;
  }
}


export async function verifyAchDescriptor(intentId, descriptor_code) {
  try {
    if (intentId.startsWith("seti_")) {
      await stripe.setupIntents.verifyMicrodeposits(
        intentId,
        { descriptor_code }
      );
    } else {
      await stripe.paymentIntents.verifyMicrodeposits(
        intentId,
        { descriptor_code }
      );
    }

    return true;
  } catch (error) {
    console.error("Error verifyAchDescriptor:", error);
    return false;
  }
}


export async function savePaymentDetailsForVerification(paymentIntentId, verificationType, userId, newPriceId, newAmounts, setUpIntentId) {
  try {
    const paymentDetailsRef = collection(firebase, COLLECTIONS.PAYMENT_VERIFICATION);
    await addDoc(paymentDetailsRef, {
      paymentIntentId,
      setUpIntentId,
      verificationType,
      userId,
      status: 'pending',
      newPriceId,
      newAmounts,
      method: 'during_verification',
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error saving payment details for verification:", error);
  }
}

export async function updatePaymentVerification(id) {
  try {
    const docRef = doc(firebase, COLLECTIONS.PAYMENT_VERIFICATION, id);
    const data = {
      status: 'verified',
      verifiedAt: serverTimestamp(),
    }
    await updateDoc(docRef, data);

    console.log("Payment verification updated successfully");
  } catch (error) {
    console.error("Error updating payment verification:", error);
  }
}

export async function getPendingPaymentVerificationByUser(userId, methods) {
  try {
    const paymentDetailsRef = collection(firebase, COLLECTIONS.PAYMENT_VERIFICATION);

    const methodList = Array.isArray(methods) ? methods : [methods];

    let response = {};

    for (const method of methodList) {
      const q = query(
        paymentDetailsRef,
        where("userId", "==", userId),
        where("status", "==", "pending"),
        where("method", "==", method)
      );

      const snapshot = await getDocs(q);
      const results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      response[method] = results;
    }

    return response;
  } catch (error) {
    console.error("Error fetching pending payment verification by user:", error);
    return {};
  }
}


// Manully update subscription 
 // step 1: Add credit in customer 
 // step 2: set the id and prod id here and run this it work

 
// export async function updatedSubscription() {
//   await stripe.subscriptionSchedules.release("sub_sched_1RyUWMHPX5Uwv3eVVZHW8rNG");

//   const subscription = await stripe.subscriptions.retrieve("sub_1RtRlHHPX5Uwv3eVi72ASEyZ");

//   const updated = await stripe.subscriptions.update("sub_1RtRlHHPX5Uwv3eVi72ASEyZ", {
//     cancel_at_period_end: false,
//     trial_end: "now",
//     items: [
//       {
//         id: subscription.items.data[0].id,
//         price: "price_1RsdVaHPX5Uwv3eVGScEMH75",
//       },
//     ],
//     promotion_code: "promo_1RyPzlHPX5Uwv3eVpXhjT2Jd",
//   });

//   console.log("✅ Updated subscription:", updated);
//   return updated;
// }

// const pricesArray = [

// ];

// export async function storeStripePrices() {
//   const paymentDetailsRef = collection(firebase, COLLECTIONS.STRIPE_PRODUCTS);
//   const batch = writeBatch(firebase);

//   pricesArray.forEach(price => {
//     if (!price?.id) return;

//     const docRef = doc(paymentDetailsRef, price.id);
//       batch.set(docRef, price, { merge: true });
//   });

//   try {
//     await batch.commit();
//     console.log(`All ${pricesArray.length} Stripe prices stored successfully!`);
//   } catch (error) {
//     console.error("Failed to store Stripe prices:", error);
//   }
// }
